/* Graph of Life brand v1.1.0 · tokens.json */
import React from 'react';
import { motion } from 'framer-motion';
import EvidenceLabel from '@/components/gol/EvidenceLabel';
import CaveatCallout from '@/components/gol/CaveatCallout';
import ProvenanceLine from '@/components/gol/ProvenanceLine';
import ProofFieldPreview from '@/components/visualizations/ProofFieldPreview';
import { GhostLink, track } from '@/components/gol/ReviewCta';

// Fixed order, always: decision question, what it does, evidence status,
// current limit, link to inspect. Everything renders from the registry.
export const ProofCard = ({ proof, emphasis = 'standard' }) => (
  <motion.article
    id={`proof-${proof.id}`}
    data-testid={`proof-card-${proof.id}`}
    initial={{ opacity: 0, y: 22 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
    className={`group gol-panel gol-panel-live relative overflow-hidden flex flex-col gap-6 p-7 md:p-9 ${
      emphasis === 'lead' ? 'gol-panel-lead' : ''
    }`}
  >

    <ProofFieldPreview proof={proof} />

    <header className="relative z-10 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-sand-50/35">Decision question</span>
        <EvidenceLabel state={proof.evidenceState} testId={`proof-state-${proof.id}`} />
      </div>
      <h3 className="font-serif text-xl md:text-2xl font-light leading-snug text-sand-50 group-hover:text-teal-100 transition-colors duration-500">
        {proof.decisionQuestion}
      </h3>
      <div className="flex items-center gap-3">
        <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-60" />
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-teal-500" />
        </span>
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-teal-400">{proof.name}</p>
      </div>
    </header>

    <div className="relative z-10 space-y-5">
      <div className="space-y-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-sand-50/35">What the system does</p>
        <p className="text-sm md:text-base leading-relaxed text-sand-50/70">{proof.whatItDoes}</p>
      </div>

      <CaveatCallout testId={`proof-limit-${proof.id}`}>{proof.currentLimit}</CaveatCallout>
    </div>

    <div className="relative z-10 flex flex-wrap items-center gap-6 pt-1">
      <GhostLink
        external
        href={proof.url}
        testId={`proof-link-${proof.id}`}
        onClick={() => track('proof_system_visit', { proof: proof.id })}
      >
        Inspect {proof.name}
      </GhostLink>
      {proof.secondary && (
        <GhostLink external href={proof.secondary.url} testId={`proof-link-${proof.id}-secondary`}>
          {proof.secondary.label}
        </GhostLink>
      )}
      {proof.tertiary && (
        <GhostLink external href={proof.tertiary.url} testId={`proof-link-${proof.id}-tertiary`}>
          {proof.tertiary.label}
        </GhostLink>
      )}
    </div>

    <details className="proof-evidence-details" data-testid={`proof-details-${proof.id}`}>
      <summary data-testid={`proof-expand-${proof.id}`}>Evidence record <span aria-hidden="true">+</span></summary>
      <p className="text-sm leading-relaxed text-sand-50/70 pt-5" data-testid={`proof-evidence-${proof.id}`}>{proof.evidenceStatus}</p>
      <ul className="flex flex-wrap gap-x-5 gap-y-2 pt-4" data-testid={`proof-metrics-${proof.id}`}>
        {proof.metrics.map(m => <li key={m} className="font-mono text-[11px] text-teal-400">{m}</li>)}
      </ul>
      <ProvenanceLine
      testId={`proof-provenance-${proof.id}`}
      source={proof.source}
      method={proof.method}
      confidence={proof.confidence}
      intendedUse={proof.intendedUse}
      lastVerified={proof.lastVerified}
      />
    </details>
  </motion.article>
);

export default ProofCard;
